// TanStack Query hooks for product selection

import {
  queryOptions,
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { trpcClient } from "../trpc";

// =============================================================================
// Types
// =============================================================================

/**
 * Extract a numeric ID from a Shopify GID string (e.g. "gid://shopify/ProductVariant/48914006540604")
 * or parse a plain numeric string. Returns undefined if the value is absent or not parseable.
 */
export function parseExternalId(
  externalId: string | null | undefined,
): number | undefined {
  if (!externalId) return undefined;
  // Try direct numeric parse first (handles plain numeric strings)
  const direct = Number(externalId);
  if (!Number.isNaN(direct)) return direct;
  // Extract trailing numeric segment from a GID-style URI
  const match = externalId.match(/\/(\d+)$/);
  if (match) return Number(match[1]);
  return undefined;
}

export interface CartItem {
  id: string;
  variantId: number;
  /** Shopify variant ID from the externalId field on ProductVariantV2. Used for order submission. */
  externalVariantId?: number;
  title: string;
  variantTitle: string;
  sku?: string;
  quantity: number;
  price: string;
  imageUrl?: string;
  // Product metadata for condition validation
  productId?: number;
  category?: string | null;
  productType?: string | null;
  vendor?: string | null;
  tags?: string[] | null;
  collectionNames?: string[] | null;
  collectionIds?: Array<number> | null;
  /**
   * Underlying collection.externalId values (Shopify GID or CSV slug).
   * These are the canonical match keys against condition.collections[].id.
   * EPD-8597 — the previous collectionIds field carries catalog_collection.id (join-table PK),
   * which is from a different domain than condition.collections[].id and never compares true.
   */
  collectionExternalIds?: Array<string> | null;
  // Variant option metadata for condition validation (e.g., Color: Black)
  options?: Array<{ name: string; value: string }> | null;
  // Inventory metadata for client-side stock validation
  tracksInventory?: boolean;
  inventoryQuantity?: number | null;
  sellWhenOutOfStock?: boolean;
  // Catalog-level inventory limit (per-product, not per-variant)
  maxInventoryLimit?: number | null;
  currentOrderedQuantity?: number | null;
  catalogProductId?: number;
}

export interface NoCatalogueCartItem {
  id: string;
  quantity: number;
  title: string;
  selections: string;
  url?: string;
  sku?: string;
  formData: Record<string, string>;
}

export interface ShippingAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  province: string;
  country: string;
  zip: string;
}

// =============================================================================
// Query Options
// =============================================================================

export function orderRequestQueryOptions(
  organizationId: string,
  orderKey: string,
  programId?: number,
) {
  return queryOptions({
    queryKey: ["orderRequest", organizationId, orderKey, programId ?? null],
    queryFn: () =>
      trpcClient.products.getOrderRequest.query({
        organizationId,
        orderKey,
        programId,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!organizationId && !!orderKey,
  });
}

export function catalogQueryOptions(
  organizationId: string,
  orderKey: string,
  catalogId: number,
) {
  return queryOptions({
    queryKey: ["catalog", organizationId, orderKey, catalogId],
    queryFn: () =>
      trpcClient.products.getCatalog.query({
        organizationId,
        orderKey,
        catalogId,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!organizationId && !!orderKey && catalogId > 0,
  });
}

export function selectionRuleQueryOptions(
  organizationId: string,
  orderKey: string,
  selectionRuleId: number,
) {
  return queryOptions({
    queryKey: ["selectionRule", organizationId, orderKey, selectionRuleId],
    queryFn: () =>
      trpcClient.products.getSelectionRule.query({
        organizationId,
        orderKey,
        selectionRuleId,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!organizationId && !!orderKey && selectionRuleId > 0,
  });
}

export function collectionsQueryOptions(
  organizationId: string,
  orderKey: string,
  catalogId: number,
) {
  return queryOptions({
    queryKey: ["collections", organizationId, orderKey, catalogId],
    queryFn: () =>
      trpcClient.products.getCollections.query({
        organizationId,
        orderKey,
        catalogId,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!organizationId && !!orderKey && catalogId > 0,
  });
}

export function productDetailsQueryOptions(
  organizationId: string,
  orderKey: string,
  catalogProductId: number,
  brandCatalogId?: number,
  pfaOrderId?: string,
) {
  return queryOptions({
    queryKey: [
      "productDetails",
      organizationId,
      orderKey,
      catalogProductId,
      brandCatalogId,
    ],
    queryFn: () => {
      return trpcClient.products.getProductDetails.query({
        organizationId,
        orderKey,
        catalogProductId,
        brandCatalogId,
        pfaOrderId,
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!organizationId && !!orderKey && catalogProductId !== 0,
  });
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch order request details.
 */
export function useOrderRequestQuery(
  organizationId: string,
  orderKey: string,
  programId?: number,
) {
  return useQuery(
    orderRequestQueryOptions(organizationId, orderKey, programId),
  );
}

/**
 * Hook to fetch catalog details.
 */
export function useCatalogQuery(
  organizationId: string,
  orderKey: string,
  catalogId: number,
) {
  return useQuery(catalogQueryOptions(organizationId, orderKey, catalogId));
}

/**
 * Hook to fetch catalog collections for filtering.
 */
export function useCollectionsQuery(
  organizationId: string,
  orderKey: string,
  catalogId: number,
) {
  return useQuery(collectionsQueryOptions(organizationId, orderKey, catalogId));
}

/**
 * Hook to fetch selection rule details.
 */
export function useSelectionRuleQuery(
  organizationId: string,
  orderKey: string,
  selectionRuleId: number,
) {
  return useQuery(
    selectionRuleQueryOptions(organizationId, orderKey, selectionRuleId),
  );
}

/**
 * Hook to fetch product details.
 */
export function useProductDetailsQuery(
  organizationId: string,
  orderKey: string,
  catalogProductId: number,
  brandCatalogId?: number,
  pfaOrderId?: string,
) {
  return useQuery(
    productDetailsQueryOptions(
      organizationId,
      orderKey,
      catalogProductId,
      brandCatalogId,
      pfaOrderId,
    ),
  );
}

export interface ProductQueryFilters {
  selectionRuleId?: number;
  conditionIndex?: number;
  collectionIds?: number[];
  categories?: string[];
  vendors?: string[];
  productTypes?: string[];
  tags?: string[];
  priceMin?: number;
  priceMax?: number;
  productIds?: number[];
}

/**
 * Returns the filter fields both as a stable query-key tuple and as a spread-ready
 * RPC payload fragment. Centralises the mapping so query-key and payload stay in sync.
 */
export function serializeProductFilters(filters?: ProductQueryFilters): {
  queryKeySegments: (number | string[] | number[] | null)[];
  payload: Omit<ProductQueryFilters, never>;
} {
  return {
    queryKeySegments: [
      filters?.selectionRuleId ?? null,
      filters?.conditionIndex ?? null,
      filters?.collectionIds ?? null,
      filters?.categories ?? null,
      filters?.vendors ?? null,
      filters?.productTypes ?? null,
      filters?.tags ?? null,
      filters?.priceMin ?? null,
      filters?.priceMax ?? null,
      filters?.productIds ?? null,
    ],
    payload: {
      selectionRuleId: filters?.selectionRuleId,
      conditionIndex: filters?.conditionIndex,
      collectionIds: filters?.collectionIds,
      categories: filters?.categories,
      vendors: filters?.vendors,
      productTypes: filters?.productTypes,
      tags: filters?.tags,
      priceMin: filters?.priceMin,
      priceMax: filters?.priceMax,
      productIds: filters?.productIds,
    },
  };
}

/**
 * Hook to fetch products with infinite scrolling.
 */
export function useProductsInfiniteQuery(
  organizationId: string,
  orderKey: string,
  catalogId: number,
  searchName: string = "",
  pageSize: number = 20,
  resourceId?: number,
  filters?: ProductQueryFilters,
  pfaOrderId?: string,
) {
  const { queryKeySegments, payload } = serializeProductFilters(filters);
  return useInfiniteQuery({
    queryKey: [
      "products",
      organizationId,
      orderKey,
      catalogId,
      searchName,
      pageSize,
      resourceId ?? null,
      ...queryKeySegments,
    ],
    queryFn: ({ pageParam = 1 }) =>
      trpcClient.products.getProducts.query({
        organizationId,
        orderKey,
        catalogId,
        searchName,
        page: pageParam,
        pageSize,
        resourceId,
        pfaOrderId,
        ...payload,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage?.hasMore) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    enabled: !!organizationId && !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch all imported products (DEFAULT catalog type) with infinite scrolling.
 * Uses clientId instead of brandCatalogId.
 */
export function useAllProductsInfiniteQuery(
  organizationId: string,
  orderKey: string,
  clientId: string,
  searchName: string = "",
  pageSize: number = 20,
  resourceId?: number,
) {
  return useInfiniteQuery({
    queryKey: [
      "allProducts",
      organizationId,
      orderKey,
      clientId,
      searchName,
      pageSize,
      resourceId,
    ],
    queryFn: ({ pageParam = 1 }) =>
      trpcClient.products.getAllProducts.query({
        organizationId,
        orderKey,
        clientId,
        searchName,
        page: pageParam,
        pageSize,
        resourceId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage?.hasMore) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    enabled: !!organizationId && !!orderKey && !!clientId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch Shopify products with cursor-based infinite scrolling.
 */
export function useShopifyProductsInfiniteQuery(
  organizationId: string,
  orderKey: string,
  projectId: number,
  searchQuery: string = "",
) {
  return useInfiniteQuery({
    queryKey: [
      "shopifyProducts",
      organizationId,
      orderKey,
      projectId,
      searchQuery,
    ],
    queryFn: async ({ pageParam }) => {
      const result = await trpcClient.products.getShopifyProducts.query({
        organizationId,
        orderKey,
        projectId,
        query: searchQuery || undefined,
        cursor: pageParam || undefined,
      });
      // Detect PFA-level errors surfaced via _meta.
      if (result && typeof result === "object" && "_meta" in result) {
        const meta = result._meta as {
          serviceError?: boolean;
          notFound?: boolean;
        };
        // "Not found" means the Shopify resource is permanently unavailable.
        // Let the empty result pass through so the UI can show a specific
        // empty state ("store unavailable") instead of a retryable error.
        if (meta.notFound) {
          return result;
        }
        // Transient service errors → throw so TanStack Query sets isError,
        // enabling the ProductGrid error/retry UI.
        if (meta.serviceError) {
          throw new Error("Product service is temporarily unavailable");
        }
      }
      return result;
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => {
      if (lastPage?.pageInfo?.hasNextPage) {
        return lastPage.pageInfo.cursor ?? undefined;
      }
      return undefined;
    },
    enabled: !!organizationId && !!orderKey && projectId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to fetch Shopify product detail (variants, options, description).
 * Called when a user clicks a product in the Shopify product grid.
 */
export function useShopifyProductDetailQuery(
  organizationId: string,
  orderKey: string,
  productId: number,
  projectId: number,
) {
  return useQuery({
    queryKey: [
      "shopifyProductDetail",
      organizationId,
      orderKey,
      productId,
      projectId,
    ],
    queryFn: () =>
      trpcClient.products.getShopifyProductDetail.query({
        organizationId,
        orderKey,
        productId,
        projectId,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: !!organizationId && !!orderKey && productId > 0 && projectId > 0,
  });
}

// =============================================================================
// Public (key-only) hooks for /orderRequests/$key preview route
// =============================================================================

/**
 * Fetch order request using just the orderKey (no org session required).
 * Used by the public catalog preview iframe route.
 */
export function useOrderRequestByKeyQuery(
  orderKey: string,
  programId?: number,
) {
  return useQuery({
    queryKey: ["orderRequestByKey", orderKey, programId ?? null],
    queryFn: () =>
      trpcClient.products.getOrderRequestByKey.query({ orderKey, programId }),
    enabled: !!orderKey,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch catalog details using just the orderKey (no org session required).
 */
export function useCatalogByKeyQuery(orderKey: string, catalogId: number) {
  return useQuery({
    queryKey: ["catalogByKey", orderKey, catalogId],
    queryFn: () =>
      trpcClient.products.getCatalogByKey.query({ orderKey, catalogId }),
    enabled: !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch selection rule details using just the orderKey (no org session required).
 */
export function useSelectionRuleByKeyQuery(
  orderKey: string,
  selectionRuleId: number,
) {
  return useQuery({
    queryKey: ["selectionRuleByKey", orderKey, selectionRuleId],
    queryFn: () =>
      trpcClient.products.getSelectionRuleByKey.query({
        orderKey,
        selectionRuleId,
      }),
    enabled: !!orderKey && selectionRuleId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch products with infinite scrolling using just the orderKey (no org session required).
 */
/**
 * Fetch all featured products for an authenticated catalog in a single request.
 * Uses isFeatured: true server-side filter (EPD-6697).
 * Only enabled when featureProductsEnabled is true on the catalog.
 */
export function useFeaturedProductsQuery(
  organizationId: string,
  orderKey: string,
  catalogId: number,
  resourceId: number | undefined,
  enabled: boolean,
  filters?: ProductQueryFilters,
  pfaOrderId?: string,
) {
  const { queryKeySegments, payload } = serializeProductFilters(filters);
  return useQuery({
    queryKey: [
      "featuredProducts",
      organizationId,
      orderKey,
      catalogId,
      resourceId,
      ...queryKeySegments,
    ],
    queryFn: () =>
      trpcClient.products.getProducts.query({
        organizationId,
        orderKey,
        catalogId,
        searchName: "",
        page: 1,
        pageSize: 250,
        resourceId,
        isFeatured: true,
        pfaOrderId,
        ...payload,
      }),
    enabled: enabled && !!organizationId && !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch all featured products for a public (key-based) catalog in a single request.
 * Uses isFeatured: true server-side filter (EPD-6697).
 * Only enabled when featureProductsEnabled is true on the catalog.
 */
export function useFeaturedProductsByKeyQuery(
  orderKey: string,
  catalogId: number,
  resourceId: number | undefined,
  enabled: boolean,
  filters?: ProductQueryFilters,
) {
  const { queryKeySegments, payload } = serializeProductFilters(filters);
  return useQuery({
    queryKey: [
      "featuredProductsByKey",
      orderKey,
      catalogId,
      resourceId,
      ...queryKeySegments,
    ],
    queryFn: () =>
      trpcClient.products.getProductsByKey.query({
        orderKey,
        catalogId,
        searchName: "",
        page: 1,
        pageSize: 250,
        resourceId,
        isFeatured: true,
        ...payload,
      }),
    enabled: enabled && !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

export function useProductsByKeyInfiniteQuery(
  orderKey: string,
  catalogId: number,
  searchName: string = "",
  pageSize: number = 20,
  resourceId?: number,
) {
  return useInfiniteQuery({
    queryKey: [
      "productsByKey",
      orderKey,
      catalogId,
      searchName,
      pageSize,
      resourceId,
    ],
    queryFn: ({ pageParam = 1 }) =>
      trpcClient.products.getProductsByKey.query({
        orderKey,
        catalogId,
        searchName,
        page: pageParam,
        pageSize,
        resourceId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage?.hasMore) return lastPageParam + 1;
      return undefined;
    },
    enabled: !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch catalog collections using just the orderKey (no org session required).
 */
export function useCollectionsByKeyQuery(orderKey: string, catalogId: number) {
  return useQuery({
    queryKey: ["collectionsByKey", orderKey, catalogId],
    queryFn: () =>
      trpcClient.products.getCollectionsByKey.query({ orderKey, catalogId }),
    enabled: !!orderKey && catalogId > 0,
    staleTime: 1000 * 60 * 5,
  });
}

export function useProductDetailByKeyQuery(
  orderKey: string,
  catalogProductId: number,
  brandCatalogId?: number,
) {
  return useQuery({
    queryKey: [
      "productDetailByKey",
      orderKey,
      catalogProductId,
      brandCatalogId,
    ],
    queryFn: () => {
      return trpcClient.products.getProductDetailByKey.query({
        orderKey,
        catalogProductId,
        brandCatalogId,
      });
    },
    enabled: !!orderKey && catalogProductId !== 0,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to resolve all boost product selection context in a single call.
 * The server looks up the offerToken from interactions, resolves the orderKey,
 * and fetches member data -- keeping sensitive tokens server-side.
 */
export function useBoostContextQuery(
  organizationId: string,
  programId: number,
  profileId: string,
  pfaOrderId: string,
) {
  return useQuery({
    queryKey: [
      "boostContext",
      organizationId,
      programId,
      profileId,
      pfaOrderId,
    ],
    queryFn: () =>
      trpcClient.products.getBoostContext.query({
        organizationId,
        programId,
        profileId,
        pfaOrderId,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes -- context doesn't change often
    enabled: !!organizationId && programId > 0 && !!profileId && !!pfaOrderId,
  });
}

/**
 * Boost context query for standalone offers (no campaign/interaction context).
 * Uses offerId + memberId instead of programId + profileId.
 */
export function useBoostContextByOfferQuery(
  organizationId: string,
  offerId: number,
  memberId: number,
  pfaOrderId: string,
) {
  return useQuery({
    queryKey: [
      "boostContextByOffer",
      organizationId,
      offerId,
      memberId,
      pfaOrderId,
    ],
    queryFn: () =>
      trpcClient.products.getBoostContextByOffer.query({
        organizationId,
        offerId,
        memberId,
        pfaOrderId,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes -- context doesn't change often
    enabled: !!organizationId && offerId > 0 && memberId > 0 && !!pfaOrderId,
  });
}

/**
 * Hook to fetch inventory settings (minimumStockLevel, maintainMinimumInventory).
 * clientId is used only as a readiness guard — the server derives it from orderKey.
 * Returns null when the order is not yet loaded or the request fails.
 */
export function useInventorySettingsQuery(
  organizationId: string,
  orderKey: string,
  clientId?: string,
  pfaOrderId?: string,
) {
  return useQuery({
    queryKey: ["inventorySettings", organizationId, orderKey],
    queryFn: () =>
      trpcClient.products.getInventorySettings.query({
        organizationId,
        orderKey,
        pfaOrderId,
      }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Only fire once the order is fully loaded (clientId presence signals the order resolved).
    enabled: !!organizationId && !!orderKey && !!clientId,
  });
}

/**
 * Member info for order submission.
 */
export interface OrderMember {
  id: number;
  name: string;
  email: string;
}

/**
 * Map cart items to the line item format expected by the order submission API.
 * Uses the Shopify variant ID (externalVariantId) when available, falling back
 * to the internal variant ID. This ensures the legacy order system receives the
 * correct Shopify variant reference (EPD-5975).
 */
export function mapCartToLineItems(cart: CartItem[]) {
  return cart.map((item) => ({
    variant_id:
      item.externalVariantId != null && !Number.isNaN(item.externalVariantId)
        ? item.externalVariantId
        : item.variantId,
    quantity: item.quantity,
    variant_title: item.variantTitle,
    title: item.title,
    price: Number(item.price),
    image: item.imageUrl,
    sku: item.sku,
  }));
}

/**
 * Hook to submit an order.
 */
export function useSubmitOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      organizationId: string;
      orderKey: string;
      orderRequestId: string;
      /** Lets the API save the shipping address to the profile (EPD-12698). */
      profileId?: string;
      member: OrderMember;
      cart: CartItem[];
      shippingAddress: ShippingAddress;
      note?: string;
    }) =>
      trpcClient.products.submitOrder.mutate({
        organizationId: input.organizationId,
        orderKey: input.orderKey,
        orderRequestId: input.orderRequestId,
        profileId: input.profileId,
        member: input.member,
        lineItems: mapCartToLineItems(input.cart),
        shippingAddress: input.shippingAddress,
        note: input.note,
      }),
    onSuccess: (_data, variables) => {
      // Invalidate order request query to refetch updated status
      queryClient.invalidateQueries({
        queryKey: [
          "orderRequest",
          variables.organizationId,
          variables.orderKey,
        ],
        exact: false,
      });
      // Invalidate boost context caches so returning to the rewards page
      // shows "Order Submitted" instead of the catalog again (EPD-7129)
      queryClient.invalidateQueries({
        queryKey: ["boostContext"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["boostContextByOffer"],
        exact: false,
      });
    },
  });
}

/**
 * Hook to submit a NoCatalogue order.
 */
export function useSubmitNoCatalogueOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      organizationId: string;
      orderKey: string;
      orderRequestId: string;
      /** Lets the API save the shipping address to the profile (EPD-12698). */
      profileId?: string;
      member: OrderMember;
      noCatalogueLineItems: Record<string, string>[];
      shippingAddress: ShippingAddress;
      note?: string;
    }) =>
      trpcClient.products.submitNoCatalogueOrder.mutate({
        organizationId: input.organizationId,
        orderKey: input.orderKey,
        orderRequestId: input.orderRequestId,
        profileId: input.profileId,
        member: input.member,
        noCatalogueLineItems: input.noCatalogueLineItems,
        shippingAddress: input.shippingAddress,
        note: input.note,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "orderRequest",
          variables.organizationId,
          variables.orderKey,
        ],
        exact: false,
      });
    },
  });
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format price with currency. Falls back to USD if the currency code is
 * invalid (PFA can return unexpected values) to avoid crashing Intl.NumberFormat.
 */
export function formatPrice(
  price: number,
  currencyCode: string = "USD",
): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(price);
  } catch {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }
}

/**
 * Format price range.
 */
export function formatPriceRange(
  priceMin: number,
  priceMax: number,
  currencyCode: string = "USD",
): string {
  if (priceMin === priceMax) {
    return formatPrice(priceMin, currencyCode);
  }
  return `${formatPrice(priceMin, currencyCode)} - ${formatPrice(priceMax, currencyCode)}`;
}

/**
 * Calculate cart total.
 */
export function calculateCartTotal(cart: CartItem[]): number {
  return cart.reduce(
    (sum, item) => sum + parseFloat(item.price) * item.quantity,
    0,
  );
}

/**
 * Calculate total items in cart.
 */
export function calculateCartQuantity(cart: CartItem[]): number {
  return cart.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Check if cart meets minimum quantity requirement.
 */
export function meetsMinQuantity(
  cart: CartItem[],
  minQuantity?: number | null,
): boolean {
  if (!minQuantity) return true;
  return calculateCartQuantity(cart) >= minQuantity;
}

/**
 * Check if cart exceeds maximum quantity.
 */
export function exceedsMaxQuantity(
  cart: CartItem[],
  maxQuantity?: number | null,
): boolean {
  if (!maxQuantity) return false;
  return calculateCartQuantity(cart) > maxQuantity;
}

/**
 * Check if cart exceeds maximum price.
 */
export function exceedsMaxPrice(
  cart: CartItem[],
  maxPrice?: number | null,
): boolean {
  if (!maxPrice) return false;
  return calculateCartTotal(cart) > maxPrice;
}

/**
 * Get variant option string from variant data.
 */
export function getVariantOptionsString(variant: {
  optionType1?: string | null;
  optionValue1?: string | null;
  optionType2?: string | null;
  optionValue2?: string | null;
  optionType3?: string | null;
  optionValue3?: string | null;
  optionType4?: string | null;
  optionValue4?: string | null;
}): string {
  const options: string[] = [];

  if (variant.optionType1 && variant.optionValue1) {
    options.push(`${variant.optionType1}: ${variant.optionValue1}`);
  }
  if (variant.optionType2 && variant.optionValue2) {
    options.push(`${variant.optionType2}: ${variant.optionValue2}`);
  }
  if (variant.optionType3 && variant.optionValue3) {
    options.push(`${variant.optionType3}: ${variant.optionValue3}`);
  }
  if (variant.optionType4 && variant.optionValue4) {
    options.push(`${variant.optionType4}: ${variant.optionValue4}`);
  }

  return options.join(", ");
}
